<script lang="ts">
// noinspection JSUnusedGlobalSymbols

import { I3pEventsEnum } from '@nuxtDatakodeUtils/types/I3pEvents.enum'
import DkOlVectorLayer from '@nuxtDatakodeUtils/components/dk-ol-map/layers/dk-ol-vector-layer.vue'
import type { FeatureCollection } from 'geojson'
import type { FeatureLike } from 'ol/Feature'
import type { ProjectionLike } from 'ol/proj'
import { inject } from 'vue'
import type olMap from 'ol/Map'
import Legend from 'ol-ext/legend/Legend'
import LegendItem from 'ol-ext/legend/Item'
import { type Extent, getCenter, getHeight, getWidth } from 'ol/extent'
import GenericLayerStyle from '~/components/I3pComponent/map/GenericLayerStyle.vue'
import { CommandVectorLayerService } from '~/services/commandVectorLayer.service'
import { CommandMapService } from '~/services/commandMap.service'
import { CommandI3pService } from '~/services/commands.i3p.service'
import { MajicEventEnum } from '~/enum/MajicEvent.enum'

export default {
    name: 'CommandLayer',
    components: { GenericLayerStyle, DkOlVectorLayer },
    props: {
        mapService: {
            type: [CommandMapService, null],
            required: true,
        },
        i3pService: { type: [CommandI3pService], required: true },
    },
    emits: ['hoverFeatures', 'clickFeatures'],
    data() {
        return {
            map: null as Map,
            geojsonData: null as FeatureCollection,
            currentEntityK: null as string,
            currentFeature: null as FeatureLike,
            editMode: false as boolean,
            dataProjection: 'EPSG:4326' as ProjectionLike,
            displayGeomAsCentroidPoint: false as boolean,
        }
    },
    computed: {
        commandVectorLayerService(): CommandVectorLayerService {
            return new CommandVectorLayerService()
        },
        title(): string {
            return this.i3pService?.type === 'command' ? this.i3pService.getLayerName() : this.i3pService.getLayerNameCommands()
        },
        isCommandService(): boolean {
            return this.i3pService?.type === 'command'
        },
        isDisplayAll(): boolean {
            return this.i3pService.getNavigationMode() === 'displayAll'
        },
    },
    mounted() {
        this.map = inject('map') as olMap
        this.dataProjection = this.i3pService.getDataProjection()
        this.i3pService.emitter
            .on(I3pEventsEnum.mapFocusBbox, (event: {
                bbox: {
                    x_min: number
                    x_max: number
                    y_min: number
                    y_max: number
                }
            }) => {
                this.setMapBoundaries(event.bbox)
            })

        this.i3pService.emitter
            .on(I3pEventsEnum.geojsonUpdated, (event: { geojsonData: FeatureCollection }) => {
                this.geojsonData = event.geojsonData
            })

        // selectedCommunesUpdated
        this.i3pService.emitter
            .on(MajicEventEnum.selectedCommunesUpdated, (event: { selectedCities: [] }) => {
                this.commandVectorLayerService.setSelectedCities(event.selectedCities)
                this.$refs?.commandLayer?.getLayer()?.changed()
                this.updateLegendVectorStyle()
            })

        this.i3pService.emitter
            .on(I3pEventsEnum.switchEditMode, (event: { editMode: boolean }) => {
                this.commandVectorLayerService.setEditMode(event.editMode)
            })
    },
    methods: {
        updateLegendVectorStyle() {
            this.commandVectorLayerService.setLegendCreated(false)
            const legend: Legend = this.mapService?.legend as Legend
            const commandsLayer = this.$refs?.commandLayer?.layer
            if (!legend || !commandsLayer)
                return

            const topItems = legend.getItems?.()?.getArray() ?? []
            if (topItems && topItems.length > 0) {
                topItems
                    .filter((it: any) => {
                        return (it.getTitle() === commandsLayer.get('title'))
                    })
                    .forEach((it: any) => legend.removeItem(it))
            }
            const source = commandsLayer.getSource()
            if (!source)
                return
            const features = source.getFeatures()
            if (!features)
                return

            const styleMap: Record<string, { title: string, style: any }> = {}

            const titles = ['isSelected', 'isHighlighted', 'isToBeDeleted']
            const feature = features[0]
            titles.forEach((title: string) => {
                if (!styleMap[title]) {
                    styleMap[title] = {
                        title: this.$translate(`majic.commands.${title}`),
                        style: this.commandVectorLayerService.getPolygonStyle(
                            feature,
                            ['isHighlighted', 'isToBeDeleted'].includes(title),
                            ['isSelected', 'isToBeDeleted'].includes(title),
                        ),
                    }
                }
            })

            const commandsLegend = new Legend({
                title: commandsLayer.get('title'),
                layer: commandsLayer,
            })
            legend.addItem(commandsLegend)

            Object.keys(styleMap)
                .forEach((key: string) => {
                    const legendItem = new LegendItem({
                        title: styleMap[key].title,
                        typeGeom: 'polyfills',
                        style: styleMap[key].style,
                    })
                    commandsLegend.addItem(legendItem)
                })

            this.commandVectorLayerService.setLegendCreated(true)
        },
        displayAll(maxZoom: number = 17): void {
            if (this.isDisplayAll)
                this.$refs.commandLayer?.displayAll(maxZoom)
        },
        hoverFeatures(features: FeatureLike[]) {
            if (this.i3pService.editMode)
                this.$emit('hoverFeatures', { features })
        },
        clickFeatures(features: FeatureLike[]) {
            this.$emit('clickFeatures', { features })
        },
        setMapBoundaries(extent: Extent) {
            const view = this.map.getView()
            const width = getWidth(extent)
            const height = getHeight(extent)

            if (width < 20 && height < 20) {
                // Very small feature → just center with a fixed zoom
                const center = getCenter(extent)
                view.animate({
                    center,
                    zoom: 17, // or whatever feels right for "street level"
                    duration: 1000,
                })
            }
            else {
                // Bigger feature → fit with padding
                view.fit(extent, {
                    duration: 1000,
                    minResolution: 2, // prevents insane zoom
                    padding: [100, 100, 100, 100], // pixels
                })
            }
        },
    },

}
</script>

<template>
    <template v-if="geojsonData && isCommandService">
        <DkOlVectorLayer
            ref="commandLayer"
            :geojson-data
            :title
            :vector-layer-service="commandVectorLayerService"
            :selected-features="(currentFeature && !create) ? [currentFeature] : []"
            :display-geom-as-centroid-point
            @hover-features="hoverFeatures($event)"
            @click-features="clickFeatures($event)"
            @update-geometry="updateGeometry($event)"
            @load-json-done="displayAll(null)"
        >
            <GenericLayerStyle
                ref="layerStyle"
                :vector-layer-service="commandVectorLayerService"
            />
        </DkOlVectorLayer>
    </template>
</template>
